import { useMemo } from "react";

// Components
import { FilterSearchTable } from "src/components/FilterSearchTable/FilterSearchTable";

// Styling
import { Theme, useTheme, styled } from "@mui/material";

// Redux (to focus selected in the timeline)
import {
  IFlight,
  IGroundEvent,
  ISearchPairing,
  IDisplayPairing,
  IPairingPosition,
  PolicyAction,
  hasPermission,
} from "@vmosolutions/oc-interfaces";
import { EventModalTypes } from "src/components/DialogModal/DialogModalWrapper";
import {
  setSelectedEvents as setSelectInTimeline,
  setFocusedEvents as setFocusInTimeline,
  setIsFlightContextOpen,
} from "src/redux/actions/flights/FlightTimeline.Actions";

import { setSelectedCrewEvents as setSelectedInCrewSched } from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { IHoveredEvent } from "src/pages/CrewSchedule/gl/CrewScheduleGL";

// Redux
import { useSelector, useDispatch } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";

// Import tableData definitions from search categories.
import { ITableData, tableData } from "./SearchCategories";
import { ICrewMember } from "src/redux/reducers/CrewScheduleReducer";
import _ from "lodash";
import { isFlight } from "../../redux/reducers/FlightTimelineReducer";

interface IAdvancedSearchProps {
  path?: string[];
  id?: string;
  windowProps?: {
    setActiveEventModal?: (activeEventModal: EventModalTypes) => void;
  };
}

export interface ITableColumn {
  id: string;
  value: string;
  index: number;
  numeric: boolean;
  label: string;
  type: string;
  width: number;
  visible: boolean;
  filter: boolean;
}

const OCAdvancedSearch = styled("div")(({ theme }) => ({
  width: "100%",
  height: "100%",
  overflowX: "auto",
  overflowY: "auto",
}));

/////////////////////////////////////////////////////
///////////  ADVANCED SEARCH FLIGHTS
/////////////////////////////////////////////////////
export const AdvancedSearch = (props: IAdvancedSearchProps): JSX.Element => {
  const { id, path, windowProps } = props;
  const { setActiveEventModal } = windowProps;
  const dispatch = useDispatch();
  const theme: Theme = useTheme();

  const allFilteredCrew = useSelector((state: IRootState) => state.crewSchedule.filteredCrew);
  //get all the crew that visible on all crew schedules so you know which pairings you can focus to
  const filteredCrew: ICrewMember[] = useMemo(() => {
    const crew: ICrewMember[] = [];
    if (!allFilteredCrew || _.isEmpty(allFilteredCrew)) {
      return [];
    }
    Object.keys(allFilteredCrew).forEach((key) => {
      crew.push(...allFilteredCrew[key]);
    });
    return _.uniqBy(crew, _.iteratee("_id"));
  }, [allFilteredCrew]);
  const mosaicPanel = useMemo(() => `${id}-panel`, [id]);

  // Retrieve timeline window ID if one is actively open.
  const interactiveState = useSelector((state: IRootState) => state.timelineFlights.interactiveState);
  const timelineWindowId = useMemo(
    () => (interactiveState ? Object.keys(interactiveState)[0] : null),
    [interactiveState]
  );

  const policies = useSelector((state: IRootState) => state.authentication?.attributes?.policies);

  /**
   * SELECT + FOCUS? events selected in the menu
   * @param events - events to select in timeline
   * @param focusSelected - whether or not to focus on those events (zoom contain)
   * @param dataType - the type of event i.e. pairings, flights etc.
   */
  const focusInTimeline = (
    events: IFlight[] | IGroundEvent[] | ISearchPairing[],
    focusSelected?: boolean,
    dataType?: string
  ): void => {
    switch (dataType) {
      case "flights":
      case "groundEvents":
        if (events.findIndex((event) => isFlight(event) && !!event.removalType) > -1) {
          //breaking out of this because you have removed flights - not sure if this is the best spot to do it
          break;
        }
        dispatch(setSelectInTimeline(events as IFlight[] | IGroundEvent[]));
        dispatch(
          setFocusInTimeline({
            events: events as IFlight[] | IGroundEvent[],
            shouldFocus: focusSelected ? "all" : "none",
          })
        );
        // Open context menu if there's a timeline open (defaults to first opened);
        // And there are events selected
        if (timelineWindowId && events.length > 0) {
          dispatch(setIsFlightContextOpen(true, timelineWindowId));
        }
        break;
      case "pairings": {
        // and create IHoveredEvents to pass to the select/focus action. // Loop through all selected search pairings as well as the assigned positions
        const visibleCrew = filteredCrew?.map((crew: ICrewMember) => crew._id);
        const selectedPairings: IHoveredEvent[] = [];
        (events as ISearchPairing[]).forEach((pairing: ISearchPairing) => {
          pairing.positions.forEach((pos: IPairingPosition) => {
            if (pos?.crew_id && visibleCrew?.includes(pos.crew_id)) {
              const selectedEvent: IHoveredEvent = {
                event: pairing as unknown as IDisplayPairing,
                crewId: pos.crew_id,
                isPlanned: false,
              };
              selectedPairings.push(selectedEvent);
            }
          });
          return pairing;
        });
        dispatch(setSelectedInCrewSched(selectedPairings, false, false, focusSelected));
        break;
      }
      default:
      // code block
    }
  };

  const allowedTableData: ITableData[] = useMemo(
    () => tableData.filter((data: ITableData) => hasPermission(policies, data.permissionResource, PolicyAction.Read)),
    [policies]
  );

  //if some how you open this window and have no allowed table data
  if (!allowedTableData.length) {
    return null;
  }
  return (
    <OCAdvancedSearch>
      <FilterSearchTable
        id={id}
        path={path}
        className="advanced_search_table"
        events={allowedTableData}
        parentId={mosaicPanel}
        uniqueKey={"_id"}
        changeSelectedRows={focusInTimeline}
        allowMultiFilter={true}
        showEventActions={true}
        setActiveEventModal={setActiveEventModal}
        showDateRangeRRule={true}
        showSavedFilters={true}
        // hideSelectionCheckboxes={true}
      />
    </OCAdvancedSearch>
  );
};
